import { $lang, getTagFromNode, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  DateTimeFieldAccuracy,
  DateTimeFieldDefinition,
  DateTimeFieldVisualization,
  DateTimeValue,
  FormFieldDefinition,
  FormOpenMode,
} from '@msdk/definitions'
import { DateTimePicker, FormField, SplitDateTimePicker } from '@msdk/ui'
import React from 'react'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'

export function DateTimeField({ node, onChange, fieldArgs }: FieldProps) {
  const fd = node.value.fieldDefinition as DateTimeFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fd, formDef)

  const { moduleName } = useParams()
  const locale = useRecoilValue($lang)

  const t = useTranslation()
  const [value, setValue] = useFieldValue(node, onChange)

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fd.entityName)

  const onFieldChange = (d: Date | null) => {
    const fieldValue = d !== null ? new DateTimeValue(d, fd.accuracyRequested, fd.behaviour) : null
    node.value.isDirty = true
    setValue(fieldValue)

    if (isDateOnly && fieldValue && currentView === lastView) {
      setTimeout(() => setOpen(false))
    }
  }

  const getDateFormat = (): any[] => {
    switch (fd.accuracyRequested) {
      case DateTimeFieldAccuracy.Date:
        return ['year', 'month', 'day']
      case DateTimeFieldAccuracy.DateTime:
        return ['year', 'month', 'day', 'hours', 'minutes', 'seconds']
      case DateTimeFieldAccuracy.Unknown:
        return ['year', 'month', 'day']
      case DateTimeFieldAccuracy.Year:
        return ['year']
      case DateTimeFieldAccuracy.YearMonth:
        return ['year', 'month']
      default:
        throw new Error('💥 Date format not supported')
    }
  }

  const isReadOnly = node.value.state === 'Readonly' || fieldArgs?.formOpenMode === FormOpenMode.Readonly
  const severity = useFieldSeverity(fd, node)

  const pickerViews = getDateFormat()
  const isDateOnly =
    !pickerViews.includes('hours') && !pickerViews.includes('minutes') && !pickerViews.includes('seconds')
  const [open, setOpen] = React.useState(false)
  const lastView = pickerViews[pickerViews.length - 1]
  const [currentView, setCurrentView] = React.useState<any>(lastView)
  const isFirstOpenRef = React.useRef(true)

  const handleViewChange = (newView: any) => {
    setCurrentView(newView)
    isFirstOpenRef.current = false
  }

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fd, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      {fd.visualization === DateTimeFieldVisualization.Split &&
      fd.accuracyRequested === DateTimeFieldAccuracy.DateTime ? (
        <SplitDateTimePicker
          dateLabel={fieldLabel}
          timeLabel={t('CMPSR_TOOLTIP_LABEL_TIME', 'Time')}
          disabled={isReadOnly}
          views={getDateFormat()}
          onChange={onFieldChange}
          value={value?.value}
          locale={locale}
          slotProps={{
            textField: {
              size: 'small',
              required: (hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly,
            },
          }}
        />
      ) : (
        <DateTimePicker
          label={fieldArgs?.showLabel && fieldLabel}
          disabled={isReadOnly}
          views={pickerViews}
          openTo={lastView}
          open={open}
          onOpen={() => {
            setOpen(true)
            setCurrentView(lastView)
            isFirstOpenRef.current = true
          }}
          onClose={() => setOpen(false)}
          onChange={onFieldChange}
          onViewChange={handleViewChange}
          locale={locale}
          value={value?.value}
          closeOnSelect={isDateOnly}
          slotProps={{
            textField: {
              size: 'small',
              required: (hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly,
            },
            actionBar: {
              actions: isDateOnly ? [] : ['accept', 'cancel'],
            },
          }}
        />
      )}

      <ValidationMessages messages={node.value.validationMessages} fieldDef={fd} node={node} />
    </FormField>
  )
}
